import React, {memo, useEffect} from 'react'
import _, {isEmpty, set} from 'lodash'
import {stripIndent} from 'common-tags'
import {Button, Dropdown, Form, Header, Label, List, Message, Modal, Radio, Tab} from 'semantic-ui-react'
import {
  withEnvironmentData,
  withErrorHandling,
  withHelmet,
  withPropsPresent,
  withResourceData,
  withResponseData,
  withServiceData,
  withVersionData
} from '../../../../../../hoc'
import {CodeEditor} from '../../../../../shared'
import {asInt, isNumber, goBack} from '../../../../../../util'
import {upsertResponse} from '../../../../../../backend'
import {useSafeState} from '../../../../../../hook'

const EXAMPLE_SCRIPT =
  stripIndent`
    // INSPECTING REQUEST
    // 1. Querystring params: req.query
    // 2. Path params: req.path
    // 3. Headers: req.headers
    // 4. Body: req.body
    // 5. Lodash: _

    // SETTING RESPONSE INFO
    // 1. Status: res.status = 200
    // 2. Headers: res.headers = {'some-customer-header': 'some-custom-value'}
    // 3. Body: res.body = 'Hello World'
    // 4. Sleep (ms): res.sleep = 5000

    // SETTING VARIABLES FOR USE IN Header/Body EJS Templates
    // 1. res.vars.foo = 'baz'
    // 2. res.vars['foo'] = true 
    // 3. res.vars.foo = { bar: 'baz' }

    // ACTIVATING RESPONSE WHEN APPROPRIATE
    //1. res.active = true/false

    res.active = false
  `

const isTemplated = (value) => {
  if (!_.isString(value)) {
    return false
  }
  return value.indexOf('<%') > -1 || value.indexOf('%>') > -1
}

const getStatusOptions = () => {
  const statuses = [100, 101, 102, 200, 201, 202, 203, 204, 205, 206, 207, 208, 226, 300, 301, 302, 303, 304, 305, 306, 307, 308, 400, 401, 402, 403, 404, 405, 406, 407, 408, 409, 410, 411, 412, 413, 414, 415, 416, 417, 418, 421, 422, 423, 424, 426, 428, 429, 431, 451, 500, 501, 502, 503, 504, 505, 506, 507, 508, 510, 511]
  return statuses.map((s) => {
    return {key: s.toString(), text: s.toString(), value: s}
  })
}

const getContentTypeOptions = (currentContentType) => {
  const contentTypes = _.union(['application/json', 'application/xml', 'plain/text', 'unknown'], [currentContentType])
  return contentTypes.map((c) => {
    return {key: c.toString(), text: c.toString(), value: c}
  })
}

const isResponsePresent = (existingResponses, status, name) => {
  return existingResponses.some((r) => r.status === status && r.name.toLowerCase() === name.toLowerCase())
}

const getHeadersMode = (headers) => {
  if (isTemplated(headers) && !_.isObject(parse(headers))) {
    return 'ejs'
  }
  return 'json'
}

const getBodyMode = (headers, body) => {
  try {
    if (isTemplated(body)) {
      return 'ejs'
    }
    headers = parse(headers) || {}
    body = parse(body)
    const contentType = headers['Content-Type'] || headers['content-type'] || ''
    const isContentTypeJson = contentType.toLowerCase().indexOf('json') > -1
    const isContentTypeXml = contentType.toLowerCase().indexOf('xml')
    const isBodyObject = !_.isNil(body) && _.isObject(body)
    if (isContentTypeJson || isBodyObject) {
      return 'json'
    }
    if (isContentTypeXml) {
      return 'xml'
    }
  } catch (e) {
  }
  return 'text'
}

const parse = (value, throwError = false) => {
  if (_.isNil(value)) {
    return value
  }
  if (_.isObject(value)) {
    return value
  }
  try {
    return JSON.parse(value)
  } catch (e) {
    if (throwError) {
      throw e
    }
    return value
  }
}

const stringify = (value) => {
  if (_.isNil(value)) {
    return ''
  }
  if (_.isString(value)) {
    return value
  }
  return JSON.stringify(value, null, 2)
}

const updateHeadersWithContentType = (updatedForm, value) => {
  const headers = parse(updatedForm.headers)
  const contentTypeKeys = _.keys(headers).filter(k => k.toLowerCase() === 'content-type')
  contentTypeKeys.forEach(ctk => delete headers[ctk])
  headers['content-type'] = value
  updatedForm.headers = stringify(headers)
  return updatedForm
}

const updateContentTypeWithHeader = (updatedForm, value) => {
  const headers = parse(value)
  const contentTypeKey = _.keys(headers).find(k => k.toLowerCase() === 'content-type')
  const contentTypeValue = contentTypeKey ? headers[contentTypeKey] : null
  if (contentTypeKey && contentTypeValue) {
    if (contentTypeValue && contentTypeValue !== updatedForm.contentType) {
      if (['application/json', 'application/xml', 'plain/text'].includes(contentTypeValue)) {
        updatedForm.contentType = contentTypeValue
      } else {
        updatedForm.contentType = 'unknown'
      }
    }
  }
  return updatedForm
}

const _ResponseUpsert = (props) => {
  let {
    service,
    version,
    environment,
    resource,
    response,
    history,
    location,
    navigate,
    mutateResource,
    mutateResponse,
    upsertResponse,
    basePath = '/c/services/:serviceId/versions/:versionId/environments/:environmentId/resources/:resourceId/responses'
  } = props

  const _isScripted = !_.isNil((response || {}).script) && !_.isEmpty((response || {}).script)

  const [state, setState] = useSafeState({
    ...{
      name: '',
      contentType: 'application/json',
      type: 'static',
      proxy: {
        from: '',
        appendResource: true
      },
      status: 200,
      weight: 0,
      sleep: 0,
      headers: {'content-type': 'application/json'},
      selected: false,
      errors: {}
    },
    serviceId: service.id,
    versionId: version.id,
    environmentId: environment.id,
    resourceId: resource.id,
    ...(response || {}),
    ...{
      isScripted: _isScripted,
      script: !_isScripted ? EXAMPLE_SCRIPT : (response || {}).script
    },
    response: (response || {})
  })

  useEffect(() => {
    setState({
      ...state,
      serviceId: service.id,
      versionId: version.id,
      environmentId: environment.id,
      resourceId: resource.id,
      ...(response || {}),
      ...{
        isScripted: _isScripted,
        script: !_isScripted ? EXAMPLE_SCRIPT : (response || {}).script
      },
      response: (response || {})
    })
  }, [service, version, environment, resource, response])

  const getPathPrefix = () => {
    return basePath
      .replace(':serviceId', service.id)
      .replace(':versionId', version.id)
      .replace(':environmentId', environment.id)
      .replace(':resourceId', resource.id)
  }

  const validate = (
    {
      name,
      contentType,
      type,
      status,
      headers,
      weight,
      proxy,
      sleep,
      isScripted,
      script,
      errors = {}
    }
  ) => {
    sleep = asInt(sleep)
    if (!name) {
      errors['response.name.required'] = 'Name required'
    } else {
      delete errors['response.name.required']
      if (name.length > 50) {
        errors['response.name.exceeds.max.length'] = 'Name cannot exceed 50 characters'
      } else {
        delete errors['response.name.exceeds.max.length']
        if (!editing && isResponsePresent(resource.responses, status, name)) {
          errors['response.name.taken'] = 'Name already present. Change name and try again'
        } else {
          delete errors['response.name.taken']
        }
      }
    }
    if (!contentType) {
      errors['response.contentType.required'] = 'Content-Type required'
    } else {
      delete errors['response.contentType.required']
      if (contentType.length > 150) {
        errors['response.name.exceeds.max.length'] = 'Content-Type cannot exceec 150 characters'
      } else {
        delete errors['response.contentType.exceeds.max.length']
      }
    }
    if (weight === null || weight === undefined || weight === '') {
      errors['response.weight.required'] = 'Weight required'
    } else {
      delete errors['response.weight.required']
      if (!isNumber(weight)) {
        errors['response.weight.numeric'] = 'Weight must be numeric'
      } else {
        delete errors['response.weight.numeric']
      }
    }

    if ((!isNumber(sleep) || !_.isInteger(sleep)) || sleep < 0) {
      errors['response.sleep.integer'] = 'Sleep must be greater than 0'
    } else {
      delete errors['response.sleep.integer']
    }

    if (!isTemplated(headers) && !_.isObject(parse(headers))) {
      errors['response.headers.invalid'] = 'Headers must be valid JSON object or EJS JSON template'
    } else {
      delete errors['response.headers.invalid']
    }
    if (type === 'proxy') {
      if (!proxy.from) {
        errors['response.proxy.from.required'] = 'Destination URL required'
      } else {
        delete errors['response.proxy.from.required']
      }
    }
    if (isScripted && !script) {
      errors['response.script.invalid'] = 'Script required'
    } else {
      delete errors['response.script.invalid']
    }
    return errors
  }

  const onAddContentType = (e, data) => {
    const updatedForm = updateHeadersWithContentType(state, data.value)
    setState({...updatedForm, ...{contentType: data.value}})
  }

  const onInputChange = (e) => {
    let updatedForm = set({...state}, e.target.name, e.target.value)
    if (e.target.name === 'contentType') {
      updatedForm = updateHeadersWithContentType(updatedForm, e.target.value)
    }
    if (e.target.name === 'headers') {
      updatedForm = updateContentTypeWithHeader(updatedForm, e.target.value)
    }
    let errors = {}
    if (state.submitted) {
      errors = validate(updatedForm)
      updatedForm = {...updatedForm, ...{errors}}
    }
    setState(updatedForm)
  }

  const onClose = () => {
    goBack(history, navigate, getPathPrefix())
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const errors = validate(state)
    if (_.keys(errors).length) {
      setState({...state, ...{errors}})
      return
    }
    state.body = parse(state.body)
    setState({...state, submitting: true})
    await upsertResponse({
      ...state,
      script: state.isScripted ? state.script : '',
      headers: parse(state.headers)
    })
    setState({...state, ...{submitting: false}})
    mutateResource()
    mutateResponse()

    if (!state.id) {
      navigate(`${getPathPrefix()}`)
    } else {
      goBack(history, navigate, getPathPrefix())
    }
  }

  const onToggleActivationType = () => {
    setState({
      ...state,
      isScripted: !state.isScripted,
      script: EXAMPLE_SCRIPT
    })
  }

  const onToggleProxyAppendResource = () => {
    setState(set({...state}, 'proxy.appendResource', !proxy.appendResource))
  }

  const getColorByHttpMethod = (method) => {
    switch (method) {
      case 'GET':
        return 'blue'
      case 'POST':
        return 'green'
      case 'PUT':
        return 'yellow'
      case 'PATCH':
        return 'orange'
      case 'DELETE':
        return 'red'
      case 'HEAD':
        return 'grey'
      case 'OPTIONS':
        return 'olive'
    }
  }

  const {
    name = '',
    type = 'static',
    weight = 0,
    status = 200,
    contentType = 'application/json',
    isScripted,
    script,
    headers,
    body,
    proxy,
    sleep = 0,
    submitting,
    errors
  } = state

  const editing = !!state.response.id
  const isProxy = type === 'proxy'
  const hasNameError = Object.keys(errors).some((k) => k.indexOf('response.name') > -1)
  const hasContentTypeError = Object.keys(errors).some((k) => k.indexOf('response.contentType') > -1)
  const hasWeightError = Object.keys(errors).some((k) => k.indexOf('response.weight') > -1)
  const hasHeadersError = Object.keys(errors).some((k) => k.indexOf('response.headers') > -1)
  const hasProxyError = Object.keys(errors).some((k) => k.indexOf('response.proxy.from') > -1)
  const hasSleepError = Object.keys(errors).some((k) => k.indexOf('response.sleep') > -1)
  const hasScriptError = Object.keys(errors).some((k) => k.indexOf('response.script') > -1)
  const hasErrors = hasNameError || hasContentTypeError || hasWeightError || hasHeadersError || hasProxyError || hasSleepError || hasScriptError
  const editMessage = isProxy ? `${status}-${name}` : name
  const statusOptions = getStatusOptions()
  const contentTypeOptions = getContentTypeOptions(contentType)

  const returnPanes = [
    {
      menuItem: 'Status/Headers',
      render: () => (
        <Tab.Pane>
          <Form error={hasHeadersError} data-testid='response-status-headeders-tab'>
            <Form.Field width='4'>
              <label>Status</label>
              <Dropdown options={statusOptions} selection search item value={status}
                        placeholder='Enter Status Code'
                        data-testid='response-status-dropdown'
                        onChange={(e, {value}) => onInputChange({
                          target: {
                            name: 'status',
                            value
                          }
                        })}/>
            </Form.Field>
            {
              !isProxy && (
                <Form.Field error={hasHeadersError}>
                  <label>Headers</label>
                  <div data-testid='response-headers-editor'>
                    <CodeEditor
                      focus={true}
                      mode={getHeadersMode(headers)}
                      theme='xcode'
                      height='150px'
                      width='100%'
                      showPrintMargin={false}
                      value={stringify(headers)}
                      onChange={(value) => {
                        onInputChange({target: {name: 'headers', value}})
                      }}
                      editorProps={{$blockScrolling: true}}
                    />
                  </div>
                </Form.Field>
              )
            }
          </Form>
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Body',
      render: () => (
        <Tab.Pane>
          <Form data-testid='response-body-tab'>
            <Form.Field>
              <label>Body</label>
              <div data-testid='response-body-editor'>
                <CodeEditor
                  focus={true}
                  mode={getBodyMode(headers, body)}
                  theme='xcode'
                  height='400px'
                  width='100%'
                  showPrintMargin={false}
                  value={stringify(body)}
                  onChange={(value) => {
                    onInputChange({target: {name: 'body', value}})
                  }}
                  highlightActiveLine={false}
                  editorProps={{$blockScrolling: true}}
                />
              </div>
            </Form.Field>
          </Form>
        </Tab.Pane>
      )
    }
  ]

  let panes = [
    {
      menuItem: 'Settings',
      render: () => (
        <Tab.Pane>
          <Form error={hasErrors} data-testid='response-settings'>
            <Form.Field error={hasNameError} required width='4'>
              <label>Name</label>
              <Form.Input placeholder='Enter response name' name='name' value={name} onChange={onInputChange}
                          data-testid='response-name-textbox'/>
            </Form.Field>
            <Form.Field width='4'>
              <label>Type</label>
              <Button.Group>
                <Button primary={!isProxy}
                        data-testid='response-static-button'
                        onClick={() => onInputChange({
                          target: {
                            name: 'type',
                            value: 'static'
                          }
                        })}>Static</Button>
                <Button.Or/>
                <Button primary={isProxy}
                        data-testid='response-proxy-button'
                        onClick={() => onInputChange({
                          target: {
                            name: 'type',
                            value: 'proxy'
                          }
                        })}>Proxy</Button>
              </Button.Group>
            </Form.Field>
            {
              isProxy && (<Form.Field error={hasProxyError} required>
                <Message data-testid='response-proxy-url-panel'>
                  <Message.Content>
                    <List relaxed>
                      <List.Item>
                        <Header as='h4'>Send Request To URL:</Header>
                        <Form.Input placeholder='Enter URL to proxy from' name='proxy.from' value={proxy.from}
                                    data-testid='response-proxy-url-textbox'
                                    onChange={onInputChange} style={{marginTop: '10px'}}/>
                      </List.Item>
                      <List.Item>
                        <Header as='h4' style={{marginBottom: '10px'}}>Include Resource Path/Query In Request</Header>
                        <Form.Field>
                          <Radio toggle checked={proxy.appendResource} onChange={() => onToggleProxyAppendResource()}
                                 data-testid='response-proxy-append-resource-radio'/>
                        </Form.Field>
                      </List.Item>
                      <List.Item>
                        <Header as='h4'>Example Proxy Request</Header>
                        <div style={{marginTop: '10px', marginBottom: '10px'}}>
                          <span style={{float: 'left'}}>
                          <Label color={getColorByHttpMethod(resource.verb)}
                                 style={{
                                   marginRight: '10px',
                                   width: '75px',
                                   textAlign: 'center'
                                 }}>{resource.verb.toUpperCase()}</Label>
                          </span>
                          <span data-testid='response-proxy-formatted-url'>
                            {`${proxy.from}${proxy.appendResource ? resource.path : ''}`}
                          </span>
                        </div>
                      </List.Item>
                    </List>
                  </Message.Content>
                </Message>
              </Form.Field>)
            }
            <Form.Field>
              <label>Scripted</label>
              <Radio toggle checked={isScripted} onChange={() => onToggleActivationType()}
                     data-testid='response-scripted-radio'/>
            </Form.Field>
            {
              !isProxy && (
                <Form.Field width='4'>
                  <label>Content-Type</label>
                  <Dropdown
                    options={contentTypeOptions}
                    allowAdditions
                    selection
                    search
                    item
                    value={contentType}
                    data-testid='response-content-type-dropdown'
                    placeholder='Select or enter Content-Type'
                    onAddItem={onAddContentType}
                    onChange={(e, {value}) => onInputChange({
                      target: {
                        name: 'contentType',
                        value
                      }
                    })}/>
                </Form.Field>
              )
            }
            <Form.Field error={hasWeightError} required width='4'>
              <label>Weight</label>
              <Form.Input placeholder='Enter weight' name='weight' value={weight} onChange={onInputChange}
                          data-testid='response-weight-textbox'/>
            </Form.Field>
            <Form.Field error={hasSleepError} required width='4'>
              <label>Sleep</label>
              <Form.Input placeholder='Enter milliseconds' name='sleep' value={sleep} onChange={onInputChange}
                          data-testid='response-sleep-textbox'/>
            </Form.Field>
          </Form>
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Script',
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.Field error={hasScriptError} data-testid='response-script-editor' required={isScripted}>
              <div>
                <CodeEditor
                  focus={true}
                  mode='javascript'
                  theme='xcode'
                  width='100%'
                  showPrintMargin={false}
                  value={script}
                  onChange={(value) => {
                    onInputChange({target: {name: 'script', value}})
                  }}
                  editorProps={{$blockScrolling: true}}
                />
              </div>
            </Form.Field>
          </Form>
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Return',
      render: () => (
        <Tab.Pane>
          <Tab defaultActiveIndex={1} menu={{secondary: true, pointing: true}} panes={returnPanes}
               data-testid='response-return-tabs'/>
        </Tab.Pane>
      )
    }
  ]

  if (isProxy) {
    panes = panes.filter(p => p.menuItem !== 'Return')
  }
  if (!isScripted) {
    panes = panes.filter(p => p.menuItem !== 'Script')
  }

  if (!state.response.name && location.pathname.endsWith('edit')) {
    return null
  }

  return (
    <div>
      <Modal open={true} closeOnDimmerClick={true} closeIcon={true} onClose={onClose} size='large'
             data-testid='response-upsert-modal'>
        <Modal.Header>{editing ? `Edit Response: ${editMessage}` : 'Add Response'}</Modal.Header>
        <Modal.Content style={{minHeight: '60vh'}}>
          <Tab panes={panes} style={{minHeight: '300px'}} data-testid='response-upsert-modal-tabs'/>
          {
            !isEmpty(errors) && (
              <Message error data-testid='response-upsert-errors'>
                <Message.Content>
                  <List bulleted>
                    {
                      Object.keys(errors).map((key) => (
                        <List.Item key={key}>{errors[key]}</List.Item>
                      ))
                    }
                  </List>
                </Message.Content>
              </Message>
            )
          }
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose} data-testid='response-upsert-cancel-button'>Close</Button>
          <Button primary onClick={onSubmit} loading={submitting}
                  data-testid='response-upsert-submit-button'>{editing ? 'Update' : 'Add'}</Button>
        </Modal.Actions>
      </Modal>
    </div>
  )
}

let UpsertResponse = (props) => {
  let {handleError} = props
  props = {
    ...props,
    history: window.history,
    upsertResponse: handleError(upsertResponse)
  }
  return (<_ResponseUpsert {...props}/>)
}

UpsertResponse = withPropsPresent(UpsertResponse, ['service', 'version', 'environment', 'resource', (props) => !props.responseId || !!props.response])
UpsertResponse = withHelmet(UpsertResponse, (props) => {
  const {response} = props
  if (response && response.id) {
    return 'Edit Response'
  }
  return 'Add Response'
})
UpsertResponse = withServiceData(UpsertResponse, 'Loading service...')
UpsertResponse = withVersionData(UpsertResponse, 'Loading version...')
UpsertResponse = withEnvironmentData(UpsertResponse, 'Loading environment...')
UpsertResponse = withResourceData(UpsertResponse, 'Loading resource...')
UpsertResponse = withResponseData(UpsertResponse, 'Loading response...')
UpsertResponse = withErrorHandling(UpsertResponse)
UpsertResponse = memo(UpsertResponse)

export {_ResponseUpsert, UpsertResponse}

export default UpsertResponse